/**
 * API 客户端 - 封装 fetch,自动加 token / 处理错误
 */

// API URL 切换器:智能 fallback
// 优先级:localStorage override → 临时 fallback → 主域 PROD
// SSL/CORS 失败的 URL 自动降级,5 分钟重试主域
const PROD_API = 'https://api.3dmfkj.top';
// Fallback 跟主域一样(避免默认 pages.dev 域 404)
const FALLBACK_API = 'https://api.3dmfkj.top';
const LOCAL_API = 'http://localhost:8787';

// URL 健康缓存
const _urlHealth = {
  [PROD_API]: null,     // null = 未测试, true = 健康, false = 失败
  [FALLBACK_API]: null,
  _lastCheck: 0,
};
const CHECK_INTERVAL = 3 * 60 * 1000;  // 3 分钟重测一次

// 测试某个 URL 是否健康(单次,3 秒超时)
async function testUrl(url) {
  if (!url) return false;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch(`${url}/api/health`, { signal: ctrl.signal });
    clearTimeout(t);
    return r.ok;
  } catch {
    return false;
  }
}

// 综合探测:返回当前应该用的 URL
async function detectBestUrl() {
  const now = Date.now();
  if (now - _urlHealth._lastCheck > CHECK_INTERVAL) {
    // 重新测试所有 URL
    _urlHealth[PROD_API] = await testUrl(PROD_API);
    _urlHealth[PROD_API] ??= await testUrl(PROD_API);
    _urlHealth[FALLBACK_API] = await testUrl(FALLBACK_API);
    _urlHealth._lastCheck = now;
  }
  // 优先用 PROD(主域),失败则用 FALLBACK
  if (_urlHealth[PROD_API] === true) return PROD_API;
  if (_urlHealth[FALLBACK_API] === true) return FALLBACK_API;
  return FALLBACK_API;  // 都没跑通 → 用 fallback(老 API 总能通)
}

export async function getApiBaseAsync() {
  // 1. localStorage override(仅 localhost 测试用,生产 URL 不用)
  try {
    const override = localStorage.getItem('fucai3d_api_base');
    if (override && (override.includes('localhost') || override.includes('127.0.0.1'))) {
      return override;
    }
  } catch {}
  // 2. 环境变量
  if (import.meta.env.VITE_API_BASE) return import.meta.env.VITE_API_BASE;
  // 3. localhost 强制走本地
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return LOCAL_API;
  }
  // 4. 智能选择(主域优先,失败 fallback)
  return await detectBestUrl();
}

// 同步版本(用于初始化)
export function getApiBase() {
  try {
    const override = localStorage.getItem('fucai3d_api_base');
    if (override && (override.includes('localhost') || override.includes('127.0.0.1'))) {
      return override;
    }
  } catch {}
  if (import.meta.env.VITE_API_BASE) return import.meta.env.VITE_API_BASE;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    // ECS / 备案域名 → 走当前 origin(nginx 反代,无跨域)
    if (h === '106.53.41.207' || h === '3dmfkj.cn' || h === '3dmfkj.top' || h === 'localhost' || h === '127.0.0.1') {
      return '';  // 相对路径,走当前 origin
    }
  }
  // 其他域名(开发/CF Pages preview)用 PROD
  return FALLBACK_API;
}

// 把所有用 API_BASE 的地方都改用 getApiBase() 实时获取
const API_BASE = getApiBase(); // 旧代码兼容(导出用)

function getToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('fucai3d_max_user') || '{}');
    // 优先读顶层 token(zustand persist 的 partialize 输出: { user, token })
    return stored?.state?.token || localStorage.getItem('fucai3d_token') || '';
  } catch {
    return '';
  }
}

function getAdminToken() {
  return localStorage.getItem('fucai3d_admin_token') || '';
}

async function request(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const userToken = getToken();
  if (userToken && !options.skipAuth) {
    headers.Authorization = `Bearer ${userToken}`;
  }

  const adminToken = getAdminToken();
  // 提取不带 query 的 path 部分
  const pathOnly = path.split('?')[0];
  // 如果 path 已经有 _t= query string,不再加 header(避免 CORS preflight)
  if (adminToken) {
    // /api/admin/* 全部加 header
    if (pathOnly.startsWith('/api/admin/') && !path.includes('_t=')) {
      headers['X-Admin-Token'] = adminToken;
    }
    // /api/payment/config 和 /api/payment/orders 是 admin 端点,需要 admin token
    else if ((pathOnly === '/api/payment/config' || pathOnly === '/api/payment/orders')
             && (options.method === 'GET' || options.method === undefined || options.method === 'POST')
             && !path.includes('_t=')) {
      headers['X-Admin-Token'] = adminToken;
    }
  }

  // 用同步 getApiBase(已做 hostname 检查:ECS IP/域名 → 相对路径,避免打到 CF Pages)
  const apiBase = getApiBase();
  const url = `${apiBase}${path}`;
  console.log('[API] fetch 请求:', url, options.method || 'GET', headers);

  // 重试:CF 边缘节点 5秒无响应,重试 5 次能扛过去
  const MAX_RETRIES = 5;
  const TIMEOUT_MS = 6000;
  let res = null;
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      res = await fetch(url, {
        ...options,
        headers,
        mode: 'cors',
        credentials: 'omit',
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (res.ok || res.status < 500) {
        lastErr = null;
        break;
      }
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
      if (attempt < MAX_RETRIES) {
        console.warn(`[API] ${attempt}/${MAX_RETRIES} 失败(${e.message}),重试中...`);
        // 短退避:500ms, 1s, 1.5s, 2s(总等待 < 6 秒)
        await new Promise(r => setTimeout(r, 500 * attempt));
      }
    }
  }

  if (!res && lastErr) {
    const errInfo = {
      message: lastErr.message,
      name: lastErr.name,
      type: lastErr.constructor?.name,
      url,
      method: options.method || 'GET',
      apiBase,
      online: navigator?.onLine,
    };
    console.error('[API] 所有重试失败:', errInfo);
    throw new Error(
      `网络异常(已重试 ${MAX_RETRIES} 次): ${lastErr.message || 'Failed to fetch'}\n` +
      `URL: ${url}\n` +
      `API: ${apiBase}`
    );
  }

  console.log('[API] fetch 响应:', res.status, res.statusText);

  let data;
  try {
    data = await res.json();
  } catch (e) {
    throw new Error(`HTTP ${res.status}: 服务异常(响应非 JSON)`);
  }

  if (!res.ok || !data.ok) {
    throw new Error(data.error || data.message || `HTTP ${res.status}`);
  }

  return data;
}

export const api = {
  // 认证
  sendCode: (phone) => request('/api/auth/send-code', {
    method: 'POST', body: JSON.stringify({ phone }), skipAuth: true,
  }),
  queryCode: (phone) => request('/api/auth/query-code', {
    method: 'POST', body: JSON.stringify({ phone }), skipAuth: true,
  }),
  // 支付 — 自助开通(老接口,内部用,直接 grant)
  upgrade: (tier, options = {}) => request('/api/payment/upgrade', {
    method: 'POST', body: JSON.stringify({ tier, ...options }),
  }),

  // 真实支付通道(wechat / alipay / mock)
  paymentCreate: (tier, method) => request('/api/payment/create', {
    method: 'POST', body: JSON.stringify({ tier, method }),
  }),
  paymentStatus: (orderId) => request(`/api/payment/status/${orderId}`),
  paymentMockPay: (orderId) => request('/api/payment/mock-pay', {
    method: 'POST', body: JSON.stringify({ order_id: orderId }),
  }),
  paymentConfig: () => request('/api/payment/config?public=1'),
  paymentSaveConfig: (cfg) => request('/api/payment/config', {
    method: 'POST', body: JSON.stringify(cfg),
  }),
  paymentOrders: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/api/payment/orders?${q}`);
  },
  login: (phone, code, referral_code, password) => {
    const body = { phone, code };
    if (referral_code) body.referral_code = referral_code;
    if (password) body.password = password;
    return request('/api/auth/login', {
      method: 'POST', body: JSON.stringify(body), skipAuth: true,
    });
  },
  loginPassword: (phone, password) => request('/api/auth/login-password', {
    method: 'POST', body: JSON.stringify({ phone, password }), skipAuth: true,
  }),
  changePassword: (oldPassword, newPassword) => request('/api/auth/change-password', {
    method: 'POST', body: JSON.stringify({ oldPassword, newPassword }),
  }),
  setPassword: (password, verifyCode) => request('/api/auth/set-password', {
    method: 'POST', body: JSON.stringify({ password, verifyCode }),
  }),
  forgotPassword: (phone, verifyCode, newPassword) => request('/api/auth/forgot-password', {
    method: 'POST', body: JSON.stringify({ phone, verifyCode, newPassword }), skipAuth: true,
  }),
  updateProfile: (body) => request('/api/auth/profile', {
    method: 'PUT', body: JSON.stringify(body),
  }),
  changePhone: (newPhone, verifyCode) => request('/api/auth/change-phone', {
    method: 'POST', body: JSON.stringify({ newPhone, verifyCode }),
  }),
  deleteAccount: (password, confirm) => request('/api/auth/delete-account', {
    method: 'POST', body: JSON.stringify({ password, confirm }),
  }),
  me: () => request('/api/auth/me', { skipAuth: true }),

  // 数据
  history: (limit = 20, offset = 0) =>
    request(`/api/ssq/history?limit=${limit}&offset=${offset}`, { skipAuth: true }),
  refresh: () => request('/api/ssq/refresh', { method: 'POST' }),
  publicSettings: () => request('/api/settings/public', { skipAuth: true }),
  importPeriods: (body) => request('/api/ssq/import', {
    method: 'POST', body: JSON.stringify(body),
  }),
  fetchLog: (body) => request('/api/ssq/fetch-log', {
    method: 'POST', body: JSON.stringify(body),
  }),

  // 定位杀(基于历史数据算必杀号)
  positionalKill: () =>
    request('/api/data/positional-kill', { skipAuth: true }),

  // 智能提醒:用户选号保存
  selections: {
    list: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/selections${q ? '?' + q : ''}`);
    },
    add: (body) => request('/api/selections', {
      method: 'POST', body: JSON.stringify(body),
    }),
    remove: (id) => request(`/api/selections/${id}`, { method: 'DELETE' }),
  },

  // 管理员
  admin: {
    stats: () => request('/api/admin/stats'),
    users: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/users?${q}`);
    },
    getUser: (id) => request(`/api/admin/users/${id}`),
    updateUser: (id, body) => request(`/api/admin/users/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
    payments: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/payments?${q}`);
    },
    createPayment: (body) => request('/api/admin/payments', {
      method: 'POST', body: JSON.stringify(body),
    }),
    logs: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/logs?${q}`);
    },
    fetchLogs: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/fetch-logs?${q}`);
    },
    diagnostics: () => request('/api/admin/diagnostics'),
    errors: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/errors?${q}`);
    },
    exportData: (type = 'all') => request(`/api/admin/export?type=${type}`),
    commissions: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/commissions?${q}`);
    },
    withdrawals: (status) => request('/api/admin/withdrawals' + (status ? `?status=${status}` : '')),
    pushList: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/admin/push/list?${q}`);
    },
    pushDelete: (id) => request(`/api/admin/push/delete?id=${id}`),
    pushClear: (body) => request('/api/admin/push/clear', { method: 'POST', body: JSON.stringify(body) }),
    approveWithdrawal: (id, notes) => request(`/api/admin/withdrawals/${id}/approve`, { method: 'POST', body: JSON.stringify({ notes }) }),
    rejectWithdrawal: (id, reason) => request(`/api/admin/withdrawals/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
    markWithdrawalPaid: (id, notes) => request(`/api/admin/withdrawals/${id}/paid`, { method: 'POST', body: JSON.stringify({ notes }) }),
    cleanup: (mode) => request('/api/admin/cleanup', {
      method: 'POST', body: JSON.stringify({ mode, confirm: 'YES_DELETE' }),
    }),
    settings: (category) => request('/api/admin/settings' + (category ? `?category=${category}` : '')),
    updateSettings: (updates) => request('/api/admin/settings', {
      method: 'POST', body: JSON.stringify({ updates }),
    }),
    // Admin Token 管理(v2.0 安全增强)
    token: {
      me: () => request('/api/admin/token/me'),
      rotate: (body = { confirm: 'ROTATE' }) => request('/api/admin/token/rotate', {
        method: 'POST', body: JSON.stringify(body),
      }),
      revoke: () => request('/api/admin/token/revoke', { method: 'POST' }),
      list: () => request('/api/admin/token/list'),
    },
    // 数据备份
    backup: {
      create: () => request('/api/admin/backup/create', { method: 'POST' }),
      list: () => request('/api/admin/backup/list'),
      restore: ({ key, confirm }) => request(`/api/admin/backup/restore?key=${encodeURIComponent(key)}&confirm=${encodeURIComponent(confirm)}`, { method: 'POST' }),
      cleanup: () => request('/api/admin/backup/cleanup', { method: 'POST' }),
    },
    // Token 首次设置 / 重置(无需 auth)
    tokenSetup: ({ token, old_token, confirm = 'SETUP' }) => request('/api/admin/token/setup', {
      method: 'POST',
      body: JSON.stringify({ token, old_token, confirm }),
      skipAuth: true,
    }),
    addPeriod: (body) => request('/api/admin/periods', {
      method: 'POST', body: JSON.stringify(body),
    }),
    batchImportPeriods: (periods) => request('/api/admin/periods/batch', {
      method: 'POST', body: JSON.stringify({ periods }),
    }),
    support: {
      tickets: (params = {}) => {
        const q = new URLSearchParams(params).toString();
        return request(`/api/admin/support/tickets?${q}`);
      },
      stats: () => request('/api/admin/support/stats'),
      ticketDetail: (id) => request(`/api/admin/support/tickets/${id}`),
      ticketReply: (id, body) => {
        // 走 admin 路由(不是 user/support/),用 admin token 验证
        const adminToken = (typeof localStorage !== 'undefined' ? localStorage.getItem('fucai3d_admin_token') : '') || '';
        return getApiBaseAsync().then(apiBase => {
          return fetch(`${apiBase}/api/admin/support/tickets/${id}/reply?_t=${encodeURIComponent(adminToken)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `content=${encodeURIComponent(body)}`,
          }).then(async r => {
            const data = await r.json().catch(() => ({}));
            if (!r.ok || !data.ok) {
              throw new Error(data.error || data.message || `HTTP ${r.status}`);
            }
            return data;
          });
        });
      },
      ticketClose: (id) => request(`/api/admin/support/tickets/${id}/close`, { method: 'POST' }),
      ticketDelete: (id) => request(`/api/admin/support/tickets/${id}`, { method: 'DELETE' }),
      ticketBulkDelete: (params = {}) => {
        const q = new URLSearchParams(params).toString();
        return request(`/api/admin/support/bulk-delete?${q}`, { method: 'POST' });
      },
    },
  },
  referrals: {
    me: () => request('/api/referrals/me'),
    team: () => request('/api/referrals/team'),
    directs: () => request('/api/referrals/directs'),
    commissions: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/referrals/commissions?${q}`);
    },
    bind: (referral_code) => request('/api/referrals/bind', {
      method: 'POST', body: JSON.stringify({ referral_code }),
    }),
    // 提现
    withdrawable: () => request('/api/referrals/withdrawable'),
    withdraw: (body) => request('/api/referrals/withdraw', { method: 'POST', body: JSON.stringify(body) }),
    withdrawals: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/referrals/withdrawals?${q}`);
    },
  },
  // 实名认证
  realName: (body) => request('/api/auth/real-name', { method: 'POST', body: JSON.stringify(body) }),
  support: {
    faqs: (category) => {
      const q = category && category !== 'all' ? `?category=${category}` : '';
      return request(`/api/support/faqs${q}`, { skipAuth: true });
    },
    faqHelpful: (id) => request('/api/support/faqs/helpful', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
    faqView: (id) => request('/api/support/faqs/view', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
    tickets: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/api/support/tickets?${q}`);
    },
    createTicket: (body) => request('/api/support/tickets', {
      method: 'POST', body: JSON.stringify(body),
    }),
    ticketDetail: (id) => request(`/api/support/tickets/${id}`),
    ticketReply: (id, content) => request(`/api/support/tickets/${id}/reply`, {
      method: 'POST', body: JSON.stringify({ content }),
    }),
    ticketClose: (id) => request(`/api/support/tickets/${id}/close`, {
      method: 'POST',
    }),
    ticketRate: (id, rating, comment) => request(`/api/support/tickets/${id}/rate`, {
      method: 'POST', body: JSON.stringify({ rating, comment }),
    }),
  },
};

// 重新 export getApiBase 已被上面的 function getApiBase 替代,删


export function setApiBase(url) {
  localStorage.setItem('fucai3d_api_base', url);
}



export { API_BASE };

// 推送
export const pushList = (params) => {
  const q = new URLSearchParams(params || {}).toString();
  return request(`/api/push/list?${q}`);
};
export const pushToday = () => request('/api/push/today');
export const pushUnread = () => request('/api/push/unread');
export const pushRead = (id) => request('/api/push/read', {
  method: 'POST', body: JSON.stringify({ id }),
});
// 社交
export const socialFollow = (target_user_id, action) => request('/api/social/follow', {
  method: 'POST', body: JSON.stringify({ target_user_id, action }),
});
export const socialFollowing = () => request('/api/social/following');
export const socialFollowers = () => request('/api/social/followers');
export const socialFeed = () => request('/api/social/feed');
export const socialPublish = (body) => request('/api/social/publish', {
  method: 'POST', body: JSON.stringify(body),
});
export const socialLeaderboard = (params) => {
  const q = new URLSearchParams(params || {}).toString();
  return request(`/api/social/leaderboard?${q}`, { skipAuth: true });
};
// 勋章
export const myBadges = () => request('/api/badges');
export const checkBadges = () => request('/api/badges/check', { method: 'POST' });
// 实战记录
export const recordsList = () => request('/api/records/list');
export const recordsAdd = (body) => request('/api/records/add', {
  method: 'POST', body: JSON.stringify(body),
});
export const recordsReconcile = () => request('/api/records/reconcile', { method: 'POST' });
